import React, { useState, useRef, useEffect } from 'react';
import './index.module.less';

interface ImageFile {
    file_url: string;
}

interface ImagePreviewProps {
    file: ImageFile;
}

const isMobile = window.navigator.userAgent.match(/(iPhone|iPod|Android|ios)/i);

const ImagePreview: React.FC<ImagePreviewProps> = ({ file }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalImageUrl, setModalImageUrl] = useState('');
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const modalImageRef = useRef<HTMLImageElement>(null);
    const touchDistanceRef = useRef<number>(0);

    // 显示大图模态框
    const showModal = (imageUrl: string) => {
        setModalImageUrl(imageUrl);
        setIsModalVisible(true);
        setScale(1); // 重置缩放
        setPosition({ x: 0, y: 0 });
    };

    // 关闭模态框
    const closeModal = () => {
        setIsModalVisible(false);
        setModalImageUrl('');
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    // 处理滚轮缩放
    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const newScale = Math.min(Math.max(0.1, scale + delta), 5); // 限制缩放范围在0.1-5之间
        setScale(newScale);
    };

    // 处理鼠标按下事件
    const handleMouseDown = (e: React.MouseEvent) => {
        if (scale <= 1) return; // 只有在放大时才能拖拽
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    // 处理鼠标移动事件
    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    // 处理鼠标松开事件
    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // 处理触摸开始事件
    const handleTouchStart = (e: React.TouchEvent) => {
        if (scale <= 1) return;

        if (e.touches.length === 1) {
            // 单指触摸，准备拖拽
            setIsDragging(true);
            setDragStart({
                x: e.touches[0].clientX - position.x,
                y: e.touches[0].clientY - position.y
            });
        } else if (e.touches.length === 2) {
            // 双指触摸，准备缩放
            const distance = Math.sqrt(
                Math.pow(e.touches[0].clientX - e.touches[1].clientX, 2) +
                Math.pow(e.touches[0].clientY - e.touches[1].clientY, 2)
            );
            touchDistanceRef.current = distance;
        }
    };

    // 处理触摸移动事件
    const handleTouchMove = (e: TouchEvent) => {
        if (e.touches.length === 1 && isDragging) {
            // 单指拖拽
            setPosition({
                x: e.touches[0].clientX - dragStart.x,
                y: e.touches[0].clientY - dragStart.y
            });
        } else if (e.touches.length === 2) {
            // 双指缩放
            const distance = Math.sqrt(
                Math.pow(e.touches[0].clientX - e.touches[1].clientX, 2) +
                Math.pow(e.touches[0].clientY - e.touches[1].clientY, 2)
            );

            if (touchDistanceRef.current > 0) {
                const scaleChange = (distance - touchDistanceRef.current) * 0.01;
                const newScale = Math.min(Math.max(0.1, scale + scaleChange), 5);
                setScale(newScale);
            }

            touchDistanceRef.current = distance;
        }
    };

    // 处理触摸结束事件
    const handleTouchEnd = () => {
        setIsDragging(false);
        touchDistanceRef.current = 0;
    };

    // 事件监听
    useEffect(() => {
        if (isModalVisible) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isModalVisible, isDragging, dragStart, position]);

    // 移动端触摸事件监听
    useEffect(() => {
        if (isModalVisible) {
            const modalImg = modalImageRef.current;
            if (modalImg) {
                modalImg.addEventListener('touchmove', handleTouchMove, { passive: false });
                modalImg.addEventListener('touchend', handleTouchEnd);
                return () => {
                    modalImg.removeEventListener('touchmove', handleTouchMove);
                    modalImg.removeEventListener('touchend', handleTouchEnd);
                };
            }
        }
    }, [isModalVisible, isDragging, scale]);

    if (!file || !file.file_url) return null;

    return (
        <>
            <div className="image-preview">
                <img
                    src={file.file_url}
                    alt="截图"
                    style={isMobile ? { width: '100px', height: '160px', objectFit: 'cover', cursor: 'pointer' } : { maxWidth: '300px', maxHeight: '200px', cursor: 'pointer' }}
                    onClick={() => showModal(file.file_url)}
                    onError={(e) => {
                        console.error('图片加载失败:', e);
                        const target = e.target as HTMLImageElement;
                        target.src = '/default-image.png';
                    }}
                />
            </div>
            {/* 图片预览模态框 */}
            {isModalVisible && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                        cursor: isDragging ? 'grabbing' : scale > 1 ? 'grab' : 'default'
                    }}
                    onClick={closeModal}
                >
                    <div
                        style={{
                            position: 'relative',
                        }}
                        onClick={(e) => e.stopPropagation()} // 防止点击图片时关闭模态框
                    >
                        <div style={{
                            position: 'absolute',
                            top: '-30px',
                            right: '-30px',
                            display: 'flex',
                            gap: '10px',
                            zIndex: 10000
                        }}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closeModal();
                                }}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                    padding: '5px 10px',
                                    borderRadius: '4px',
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div
                            style={{
                                maxWidth: '350px',
                                maxHeight: '300px',
                                overflow: 'hidden'
                            }}
                        >
                            <img
                                ref={modalImageRef}
                                src={modalImageUrl}
                                alt="预览"
                                style={{
                                    maxWidth: '350px',
                                    maxHeight: '300px',
                                    objectFit: 'contain',
                                    transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                                    transition: isDragging ? 'none' : 'transform 0.1s',
                                    cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                                }}
                                onWheel={handleWheel}
                                onMouseDown={handleMouseDown}
                                onTouchStart={handleTouchStart}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ImagePreview;